(() => {
  'use strict';

  const SESSION_KEY =
    'peernest_supabase_session';

  const configElement =
    document.getElementById(
      'pn-supabase-config'
    );

  if (!configElement) {
    console.error(
      '[PeerNest] Supabase config missing.'
    );
    return;
  }


  let config;

  try {
    config =
      JSON.parse(
        configElement.textContent
      );
  }
  catch (error) {
    console.error(
      '[PeerNest] Invalid Supabase config.',
      error
    );
    return;
  }


  const baseURL =
    String(
      config.url || ''
    ).replace(/\/+$/, '');


  const publishableKey =
    String(
      config.publishableKey || ''
    );


  if (
    !baseURL ||
    !publishableKey
  ) {
    console.error(
      '[PeerNest] Supabase URL/key missing.'
    );
    return;
  }



  /* ========================================================
     SESSION
  ======================================================== */

  function readSession() {

    try {

      const raw =
        localStorage.getItem(
          SESSION_KEY
        );


      if (!raw) {
        return null;
      }


      const session =
        JSON.parse(raw);


      if (
        !session ||
        !session.access_token
      ) {
        return null;
      }


      return session;

    }
    catch {
      return null;
    }

  }


  function writeSession(
    session
  ) {

    if (!session) {

      localStorage.removeItem(
        SESSION_KEY
      );

      return;

    }


    const saved = { ...session };
    // GoTrue normally returns expires_at, but some flows only
    // include expires_in. Never treat that token as perpetual.
    if (!Number(saved.expires_at) && Number(saved.expires_in) > 0) {
      saved.expires_at = Math.floor(Date.now() / 1000)
        + Math.floor(Number(saved.expires_in));
    }
    localStorage.setItem(SESSION_KEY, JSON.stringify(saved));

  }


  function clearSession() {

    localStorage.removeItem(
      SESSION_KEY
    );

  }



  /* ========================================================
     AUTH REQUEST
  ======================================================== */

  async function authRequest(
    path,
    {
      method = 'POST',
      body = null,
      accessToken = null
    } = {}
  ) {

    const headers = {
      apikey:
        publishableKey,

      'Content-Type':
        'application/json',

      Accept:
        'application/json'
    };


    if (accessToken) {

      headers.Authorization =
        `Bearer ${accessToken}`;

    }


    const response =
      await fetch(
        `${baseURL}/auth/v1/${path}`,
        {
          method,
          headers,
          body:
            body
              ? JSON.stringify(body)
              : undefined
        }
      );


    const payload =
      await response
        .json()
        .catch(() => ({}));


    if (!response.ok) {

      const error = new Error(
        payload.msg ||
        payload.message ||
        payload.error_description ||
        payload.error ||
        'Authentication request failed.'
      );
      error.status = response.status;
      error.code = String(payload.code || payload.error_code || '');
      throw error;

    }


    return payload;

  }



  /* ========================================================
     SIGN UP
  ======================================================== */

  async function signUp({
    email,
    password,
    fullName = '',
    captchaToken = ''
  }) {

    const result =
      await authRequest(
        'signup?redirect_to=' +
          encodeURIComponent(
            window.location.origin + '/'
          ),
        {
          body:{
            email,
            password,

            gotrue_meta_security:{
              captcha_token:
                captchaToken
            },

            data:{
              full_name:
                fullName
            }
          }
        }
      );


    if (
      result.access_token
    ) {

      writeSession(
        result
      );

    }


    document.dispatchEvent(
      new CustomEvent(
        'peernest:auth-changed'
      )
    );


    return result;

  }



  /* ========================================================
     SIGN IN
  ======================================================== */

  async function signIn({
    email,
    password,
    captchaToken = ''
  }) {

    const result =
      await authRequest(
        'token?grant_type=password',
        {
          body:{
            email,
            password,

            gotrue_meta_security:{
              captcha_token:
                captchaToken
            }
          }
        }
      );


    writeSession(
      result
    );


    document.dispatchEvent(
      new CustomEvent(
        'peernest:auth-changed'
      )
    );


    return result;

  }



  /* ========================================================
     SESSION RECOVERY — MOBILE SAFE
     Never erase tokens merely because the network failed.
     Serialize concurrent refreshes in the same browser tab.
  ======================================================== */

  let sessionRefreshPromise = null;

  function isInvalidRefreshToken(error) {
    const status = Number(error?.status);
    const explanation = String(
      (error?.code || '') + ' ' + (error?.message || '')
    ).toLowerCase();
    return [400, 401, 403].includes(status) &&
      /(invalid.grant|refresh.token|invalid.token|token.expired|session.not.found|unauthorized)/
        .test(explanation);
  }

  function isUnauthorizedAuthError(error) {
    return [401, 403].includes(Number(error?.status));
  }

  function temporaryAuthError(error) {
    // A failed mobile network request, 429, or a 5xx is not logout.
    if (error && (typeof error === 'object' || typeof error === 'function')) {
      error.pnAuthTransient = true;
      return error;
    }
    const transient = new Error('Authentication check temporarily unavailable.');
    transient.pnAuthTransient = true;
    return transient;
  }

  async function refreshSession() {
    if (sessionRefreshPromise) return sessionRefreshPromise;
    const work = (async () => {
      const current = readSession();
      if (!current?.refresh_token) return null;
      try {
        const result = await authRequest(
          'token?grant_type=refresh_token',
          { body: { refresh_token: current.refresh_token } }
        );
        // Another tab can write a newer token, and an explicit sign-out
        // can remove the session while the request is in flight.
        const latest = readSession();
        if (!latest?.refresh_token) return null;
        if (latest.refresh_token !== current.refresh_token) return latest;
        writeSession(result);
        return readSession();
      } catch (error) {
        if (isInvalidRefreshToken(error)) {
          const latest = readSession();
          if (latest?.refresh_token &&
              latest.refresh_token !== current.refresh_token) return latest;
          clearSession();
          return null;
        }
        throw temporaryAuthError(error);
      }
    })();
    sessionRefreshPromise = work;
    try {
      return await work;
    } finally {
      if (sessionRefreshPromise === work) sessionRefreshPromise = null;
    }
  }

  async function getSession() {
    const session = readSession();
    if (!session) return null;
    const expiry = Number(session.expires_at);
    if (expiry && Date.now() >= (expiry - 60) * 1000) {
      return refreshSession();
    }
    return session;
  }

  async function getCurrentUser() {
    const session = await getSession();
    if (!session?.access_token) return null;
    try {
      return await authRequest('user', {
        method: 'GET', accessToken: session.access_token
      });
    } catch (error) {
      if (!isUnauthorizedAuthError(error)) {
        throw temporaryAuthError(error);
      }
      const refreshed = await refreshSession();
      if (!refreshed?.access_token) return null;
      try {
        return await authRequest('user', {
          method: 'GET', accessToken: refreshed.access_token
        });
      } catch (retryError) {
        if (isUnauthorizedAuthError(retryError)) {
          clearSession();
          return null;
        }
        throw temporaryAuthError(retryError);
      }
    }
  }


  /* ========================================================
     PASSWORD RECOVERY
  ======================================================== */

  async function requestPasswordReset(
    email,
    redirectTo =
      window.location.origin +
      '/pages/reset-password?view=reset-password',
    captchaToken = ''
  ) {

    return authRequest(
      'recover?redirect_to=' +
      encodeURIComponent(
        redirectTo
      ),
      {
        body:{
          email,

          gotrue_meta_security:{
            captcha_token:
              captchaToken
          }
        }
      }
    );

  }



  async function updatePassword(
    password
  ) {

    const session =
      await getSession();

    if (
      !session?.access_token
    ) {
      throw new Error(
        'Your password reset link is invalid or has expired.'
      );
    }

    return authRequest(
      'user',
      {
        method:'PUT',
        accessToken:
          session.access_token,
        body:{
          password
        }
      }
    );

  }



  /* ========================================================
     SIGN OUT
  ======================================================== */

  async function signOut() {

    const session =
      readSession();


    try {

      if (
        session?.access_token
      ) {

        await authRequest(
          'logout',
          {
            accessToken:
              session.access_token
          }
        );

      }

    }

    catch (error) {

      console.warn(
        '[PeerNest] Remote logout failed.',
        error
      );

    }


    clearSession();


    document.dispatchEvent(
      new CustomEvent(
        'peernest:auth-changed'
      )
    );

  }



  /* ========================================================
     DATABASE REQUEST
  ======================================================== */

  /* SEO PRIVACY HOOK: Shopify-first draft before selected visibility writes.
     Important: This is browser routing, not an RLS/DB security boundary.
     Deploy and test secure Edge Function BEFORE installing this file. */
  async function protectSeoBeforeSensitiveWrite(
    table,
    query,
    options,
    session
  ) {
    const body = options.body || {};
    const method = String(options.method || 'GET').toUpperCase();
    let payload = null;

    if (method === 'POST' && table === 'rpc/set_my_mentor_visibility' && body.p_visible === false) {
      payload = {mode:'protect',action:'mentor_self_hide'};
    }
    else if (method === 'POST' && table === 'rpc/admin_set_mentor_visibility' && body.make_visible === false) {
      payload = {mode:'protect',action:'mentor_admin_hide',mentor_id:body.target_mentor_id};
    }
    else if (method === 'POST' && table === 'rpc/admin_set_mentor_status' && body.new_status === 'suspended') {
      payload = {mode:'protect',action:'mentor_admin_suspend',mentor_id:body.target_mentor_id};
    }
    else if (method === 'POST' && (table === 'rpc/admin_delete_college' || table === 'rpc/admin_archive_college')) {
      payload = {mode:'protect',action:'college_delete',college_id:body.p_college_id};
    }
    else if (method === 'PATCH' && table === 'colleges' &&
      (body.is_published === false || typeof body.slug === 'string')) {
      const singleId = new URLSearchParams(query).get('id') || '';
      if (!/^eq\.[0-9a-fA-F-]{36}$/.test(singleId)) {
        throw new Error('College ID is needed for safe SEO unpublishing.');
      }
      payload = {
        mode:'protect',action:'college_update',
        college_id:singleId.slice(3),
        ...(Object.prototype.hasOwnProperty.call(body,'is_published') ?
          {next_is_published:body.is_published} : {}),
        ...(typeof body.slug === 'string' ? {proposed_slug:body.slug} : {})
      };
    }

    if (!payload) return;
    if (!session?.access_token) throw new Error('Sign in before changing profile visibility.');

    const response = await fetch(
      `${baseURL}/functions/v1/peernest-seo-protect`,
      {
        method:'POST',
        headers:{
          apikey:publishableKey,
          Authorization:`Bearer ${session.access_token}`,
          'Content-Type':'application/json'
        },
        body:JSON.stringify(payload)
      }
    );
    const value = await response.json().catch(() => ({}));
    if (!response.ok || value.result !== 'SHOPIFY_ENTRIES_VERIFIED_DRAFT') {
      throw new Error('SEO privacy protection failed. Visibility was not changed.');
    }
  }


  async function request(
    table,
    query = '',
    options = {}
  ) {

    const endpoint =
      `${baseURL}/rest/v1/${table}` +
      (
        query
          ? `?${query}`
          : ''
      );


    const session =
      await getSession();


    // Do not change the existing Supabase RPC unless Shopify's SEO page is DRAFT.
    await protectSeoBeforeSensitiveWrite(table, query, options, session);


    const headers = {

      apikey:
        publishableKey,

      Accept:
        'application/json',

      ...(options.headers || {})

    };


    if (
      session?.access_token
    ) {

      headers.Authorization =
        `Bearer ${session.access_token}`;

    }


    if (
      options.body !== undefined
    ) {

      headers['Content-Type'] =
        'application/json';

    }


    const response =
      await fetch(
        endpoint,
        {
          method:
            options.method ||
            'GET',

          headers,

          body:
            options.body !== undefined
              ? JSON.stringify(
                  options.body
                )
              : undefined
        }
      );


    if (!response.ok) {

      const detail =
        await response
          .text()
          .catch(() => '');


      throw new Error(
        `[PeerNest] ${table} failed ` +
        `(${response.status}): ${detail}`
      );

    }


    if (
      response.status === 204
    ) {
      return null;
    }


    const text =
      await response.text();


    return text
      ? JSON.parse(text)
      : null;

  }




  /* ========================================================
     PN_SOCIAL_OAUTH_SUPPORT
  ======================================================== */

  function signInWithOAuth(
    provider,
    redirectTo =
      window.location.origin +
      '/pages/login'
  ) {

    if (
      !['google','apple'].includes(
        provider
      )
    ) {
      throw new Error(
        'Unsupported authentication provider.'
      );
    }


    const url =
      new URL(
        `${baseURL}/auth/v1/authorize`
      );


    url.searchParams.set(
      'provider',
      provider
    );


    url.searchParams.set(
      'redirect_to',
      redirectTo
    );


    window.location.assign(
      url.toString()
    );

  }



  function consumeOAuthRedirect() {

    const hash =
      window.location.hash;


    if (
      !hash ||
      hash.length < 2
    ) {
      return null;
    }


    const params =
      new URLSearchParams(
        hash.slice(1)
      );


    const error =
      params.get(
        'error_description'
      ) ||
      params.get(
        'error'
      );


    if (error) {

      history.replaceState(
        {},
        document.title,
        window.location.pathname +
        window.location.search
      );


      throw new Error(
        error
      );

    }


    const accessToken =
      params.get(
        'access_token'
      );


    if (!accessToken) {
      return null;
    }


    const expiresIn =
      Number(
        params.get(
          'expires_in'
        ) ||
        3600
      );


    const session = {

      access_token:
        accessToken,

      refresh_token:
        params.get(
          'refresh_token'
        ) ||
        '',

      token_type:
        params.get(
          'token_type'
        ) ||
        'bearer',

      expires_in:
        expiresIn,

      expires_at:
        Math.floor(
          Date.now() / 1000
        ) +
        expiresIn

    };


    localStorage.setItem(
      SESSION_KEY,
      JSON.stringify(
        session
      )
    );


    history.replaceState(
      {},
      document.title,
      window.location.pathname +
      window.location.search
    );


    document.dispatchEvent(
      new CustomEvent(
        'peernest:auth-changed',
        {
          detail:{
            session
          }
        }
      )
    );


    return session;

  }


  /* ========================================================
     PROFILE
  ======================================================== */

  async function getMyProfile(verifiedUser = null) {

    // Existing no-argument callers still validate their user.
    // The homepage passes its already-verified auth user to avoid
    // a duplicate /auth/v1/user request during first render.
    const user =
      verifiedUser?.id
        ? verifiedUser
        : await getCurrentUser();


    if (!user?.id) {
      return null;
    }


    const rows =
      await request(
        'profiles',
        [
          'select=id,email,full_name,role,account_status,avatar_url,phone,bio,institution,program,graduation_year,skills,linkedin_url,github_url,portfolio_url,resume_path,created_at,updated_at',
          `id=eq.${encodeURIComponent(
            user.id
          )}`,
          'limit=1'
        ].join('&')
      );


    return (
      Array.isArray(rows)
        ? rows[0]
        : null
    ) || null;

  }





  async function updateMyProfile({
    full_name,
    phone = null
  }) {

    const result =
      await request(
        'rpc/update_my_profile',
        '',
        {
          method: 'POST',

          headers: {
            Prefer: 'return=representation'
          },

          body: {
            p_full_name:
              String(
                full_name || ''
              ).trim(),

            p_phone:
              String(
                phone || ''
              ).trim() || null
          }
        }
      );


    return (
      Array.isArray(result)
        ? result[0]
        : result
    ) || null;

  }



  /* ========================================================
     PHASE 3 — PROFILE PORTFOLIO
  ======================================================== */

  async function updateMyProfileDetails(
    data = {}
  ) {

    const result =
      await request(
        'rpc/update_my_profile_details',
        '',
        {
          method:'POST',

          headers:{
            Prefer:'return=representation'
          },

          body:{
            p_full_name:
              String(
                data.full_name || ''
              ).trim(),

            p_phone:
              String(
                data.phone || ''
              ).trim() || null,

            p_bio:
              String(
                data.bio || ''
              ).trim() || null,

            p_institution:
              String(
                data.institution || ''
              ).trim() || null,

            p_program:
              String(
                data.program || ''
              ).trim() || null,

            p_graduation_year:
              data.graduation_year
                ? Number(
                    data.graduation_year
                  )
                : null,

            p_skills:
              Array.isArray(
                data.skills
              )
                ? data.skills
                : [],

            p_linkedin_url:
              String(
                data.linkedin_url || ''
              ).trim() || null,

            p_github_url:
              String(
                data.github_url || ''
              ).trim() || null,

            p_portfolio_url:
              String(
                data.portfolio_url || ''
              ).trim() || null
          }
        }
      );


    return (
      Array.isArray(result)
        ? result[0]
        : result
    ) || null;

  }


  async function profileStorageRequest(
    path,
    {
      method = 'GET',
      body,
      headers = {}
    } = {}
  ) {

    const session =
      await getSession();


    if (!session?.access_token) {
      throw new Error(
        'You must be signed in.'
      );
    }


    const response =
      await fetch(
        `${baseURL}/storage/v1/${path}`,
        {
          method,

          headers:{
            apikey:
              publishableKey,

            Authorization:
              `Bearer ${session.access_token}`,

            ...headers
          },

          body
        }
      );


    if (!response.ok) {

      const detail =
        await response
          .text()
          .catch(() => '');


      throw new Error(
        `[PeerNest Storage] ${response.status}: ${detail}`
      );

    }


    if (
      response.status === 204
    ) {
      return null;
    }


    const contentType =
      response.headers
        .get('content-type') ||
      '';


    if (
      contentType.includes(
        'application/json'
      )
    ) {
      return response.json();
    }


    return response.text();

  }


  async function setMyAvatarURL(
    avatarURL
  ) {

    const result =
      await request(
        'rpc/set_my_avatar_url',
        '',
        {
          method:'POST',

          body:{
            p_avatar_url:
              avatarURL || null
          }
        }
      );


    return (
      Array.isArray(result)
        ? result[0]
        : result
    ) || null;

  }


  async function uploadMyAvatar(
    file
  ) {

    const user =
      await getCurrentUser();


    if (!user?.id) {
      throw new Error(
        'You must be signed in.'
      );
    }


    if (
      ![
        'image/jpeg',
        'image/png',
        'image/webp'
      ].includes(
        file?.type
      )
    ) {
      throw new Error(
        'Use a JPG, PNG or WebP image.'
      );
    }


    if (
      file.size >
      5 * 1024 * 1024
    ) {
      throw new Error(
        'Profile photo must be under 5 MB.'
      );
    }


    const objectPath =
      `${user.id}/avatar`;


    await profileStorageRequest(
      `object/profile-avatars/${user.id}/avatar`,
      {
        method:'POST',

        headers:{
          'Content-Type':
            file.type,

          'x-upsert':'true'
        },

        body:file
      }
    );


    const publicURL =
      `${baseURL}/storage/v1/object/public/` +
      `profile-avatars/${objectPath}` +
      `?v=${Date.now()}`;


    await setMyAvatarURL(
      publicURL
    );


    return publicURL;

  }



  /* ========================================================
     PHASE 10 — ADMIN COLLEGE MEDIA
     Frontend helper only.
     Reuses authenticated profile storage transport.
     ======================================================== */

  async function uploadCollegeMedia(
    file,
    collegeKey,
    kind = 'cover'
  ) {

    const user =
      await getCurrentUser();


    if (!user?.id) {

      throw new Error(
        'You must be signed in.'
      );

    }


    if (
      ![
        'image/jpeg',
        'image/png',
        'image/webp'
      ].includes(
        file?.type
      )
    ) {

      throw new Error(
        'Use a JPG, PNG or WebP image.'
      );

    }


    if (
      file.size >
      5 * 1024 * 1024
    ) {

      throw new Error(
        'College images must be under 5 MB.'
      );

    }


    const safeKind =
      kind ===
      'logo'
        ? 'logo'
        : 'cover';


    const safeCollegeKey =
      String(
        collegeKey ||
        'draft'
      )
        .trim()
        .toLowerCase()
        .replace(
          /[^a-z0-9_-]+/g,
          '-'
        )
        .replace(
          /^-+|-+$/g,
          ''
        ) ||
      'draft';


    const extension =
      file.type ===
      'image/png'
        ? 'png'
        : file.type ===
          'image/webp'
            ? 'webp'
            : 'jpg';


    /*
      Keep files under the authenticated admin user's
      existing storage folder so the same storage policy
      used by profile images can be reused.
    */

    const objectPath =
      `${user.id}/college-media/` +
      `${safeCollegeKey}/` +
      `${safeKind}.${extension}`;


    await profileStorageRequest(
      `object/profile-avatars/${objectPath}`,
      {
        method:'POST',

        headers:{
          'Content-Type':
            file.type,

          'x-upsert':
            'true'
        },

        body:
          file
      }
    );


    return (
      `${baseURL}/storage/v1/object/public/` +
      `profile-avatars/${objectPath}` +
      `?v=${Date.now()}`
    );

  }



  async function removeMyAvatar() {

    const user =
      await getCurrentUser();


    if (!user?.id) {
      throw new Error(
        'You must be signed in.'
      );
    }


    await profileStorageRequest(
      'object/profile-avatars',
      {
        method:'DELETE',

        headers:{
          'Content-Type':
            'application/json'
        },

        body:
          JSON.stringify({
            prefixes:[
              `${user.id}/avatar`
            ]
          })
      }
    );


    await setMyAvatarURL(
      null
    );


    return true;

  }


  async function setMyResumePath(
    resumePath
  ) {

    const result =
      await request(
        'rpc/set_my_resume_path',
        '',
        {
          method:'POST',

          body:{
            p_resume_path:
              resumePath || null
          }
        }
      );


    return (
      Array.isArray(result)
        ? result[0]
        : result
    ) || null;

  }


  async function uploadMyResume(
    file
  ) {

    const user =
      await getCurrentUser();


    if (!user?.id) {
      throw new Error(
        'You must be signed in.'
      );
    }


    if (
      file?.type !==
      'application/pdf'
    ) {
      throw new Error(
        'Resume must be a PDF.'
      );
    }


    if (
      file.size >
      10 * 1024 * 1024
    ) {
      throw new Error(
        'Resume must be under 10 MB.'
      );
    }


    const objectPath =
      `${user.id}/resume.pdf`;


    await profileStorageRequest(
      `object/profile-resumes/${user.id}/resume.pdf`,
      {
        method:'POST',

        headers:{
          'Content-Type':
            'application/pdf',

          'x-upsert':'true'
        },

        body:file
      }
    );


    await setMyResumePath(
      objectPath
    );


    return objectPath;

  }


  async function getMyResumeURL() {

    const profile =
      await getMyProfile();


    if (!profile?.resume_path) {
      return null;
    }


    const result =
      await profileStorageRequest(
        `object/sign/profile-resumes/${profile.resume_path}`,
        {
          method:'POST',

          headers:{
            'Content-Type':
              'application/json'
          },

          body:
            JSON.stringify({
              expiresIn:600
            })
        }
      );


    const signed =
      result?.signedURL ||
      result?.signedUrl ||
      null;


    if (!signed) {
      return null;
    }


    return signed.startsWith('http')
      ? signed
      : `${baseURL}/storage/v1${signed}`;

  }


  async function removeMyResume() {

    const user =
      await getCurrentUser();


    if (!user?.id) {
      throw new Error(
        'You must be signed in.'
      );
    }


    await profileStorageRequest(
      'object/profile-resumes',
      {
        method:'DELETE',

        headers:{
          'Content-Type':
            'application/json'
        },

        body:
          JSON.stringify({
            prefixes:[
              `${user.id}/resume.pdf`
            ]
          })
      }
    );


    await setMyResumePath(
      null
    );


    return true;

  }


  async function getMyProjects() {

    const user =
      await getCurrentUser();


    if (!user?.id) {
      return [];
    }


    return (
      await request(
        'profile_projects',
        [
          'select=id,user_id,title,description,project_url,github_url,technologies,sort_order,created_at,updated_at',

          `user_id=eq.${encodeURIComponent(
            user.id
          )}`,

          'order=sort_order.asc,created_at.desc'
        ].join('&')
      )
    ) || [];

  }


  function projectPayload(
    data = {}
  ) {

    return {
      title:
        String(
          data.title || ''
        ).trim(),

      description:
        String(
          data.description || ''
        ).trim() || null,

      project_url:
        String(
          data.project_url || ''
        ).trim() || null,

      github_url:
        String(
          data.github_url || ''
        ).trim() || null,

      technologies:
        Array.isArray(
          data.technologies
        )
          ? data.technologies
          : []
    };

  }


  async function createMyProject(
    data
  ) {

    const user =
      await getCurrentUser();


    if (!user?.id) {
      throw new Error(
        'You must be signed in.'
      );
    }


    const result =
      await request(
        'profile_projects',
        '',
        {
          method:'POST',

          headers:{
            Prefer:'return=representation'
          },

          body:{
            user_id:
              user.id,

            ...projectPayload(
              data
            )
          }
        }
      );


    return (
      Array.isArray(result)
        ? result[0]
        : result
    ) || null;

  }


  async function updateMyProject(
    id,
    data
  ) {

    const user =
      await getCurrentUser();


    if (!user?.id) {
      throw new Error(
        'You must be signed in.'
      );
    }


    const result =
      await request(
        'profile_projects',
        [
          `id=eq.${encodeURIComponent(
            id
          )}`,

          `user_id=eq.${encodeURIComponent(
            user.id
          )}`
        ].join('&'),
        {
          method:'PATCH',

          headers:{
            Prefer:'return=representation'
          },

          body:
            projectPayload(
              data
            )
        }
      );


    return (
      Array.isArray(result)
        ? result[0]
        : result
    ) || null;

  }


  async function deleteMyProject(
    id
  ) {

    const user =
      await getCurrentUser();


    if (!user?.id) {
      throw new Error(
        'You must be signed in.'
      );
    }


    await request(
      'profile_projects',
      [
        `id=eq.${encodeURIComponent(
          id
        )}`,

        `user_id=eq.${encodeURIComponent(
          user.id
        )}`
      ].join('&'),
      {
        method:'DELETE',

        headers:{
          Prefer:'return=minimal'
        }
      }
    );


    return true;

  }


  /* ========================================================
     PUBLIC COLLEGE DATA
  ======================================================== */

  // Fetch every published college in small, stable pages. Batch size is
  // NOT a total-results cap. PostgREST installations often cap one response.
  async function getPublishedColleges() {

    const batchSize = 200;
    let offset = 0;
    const colleges = [];
    const seenIds = new Set();

    for (;;) {
      const page = await request(
        'colleges',
        [
          'select=*',
          'is_published=eq.true',
          'order=name.asc,id.asc',
          `limit=${batchSize}`,
          `offset=${offset}`
        ].join('&')
      );

      if (!Array.isArray(page)) {
        throw new Error('[PeerNest] Invalid published colleges response.');
      }

      for (const college of page) {
        if (!seenIds.has(college.id)) {
          seenIds.add(college.id);
          colleges.push(college);
        }
      }

      if (page.length < batchSize) {
        return colleges;
      }

      offset += page.length;
    }

  }


  async function searchPeerNestColleges(
    query,
    limit = 8
  ) {

    const clean =
      String(
        query || ''
      ).trim();


    if (
      clean.length < 2
    ) {
      return [];
    }


    const result =
      await request(
        'rpc/search_college_registry',
        '',
        {
          method:'POST',

          body:{
            p_query:
              clean,

            p_limit:
              Math.max(
                1,
                Math.min(
                  Number(limit) || 8,
                  12
                )
              )
          }
        }
      );


    return Array.isArray(result)
      ? result
      : [];

  }



  async function requestMissingCollege(
    data = {}
  ) {

    const result =
      await request(
        'rpc/request_missing_college',
        '',
        {
          method:'POST',

          headers:{
            Prefer:'return=representation'
          },

          body:{
            p_name:
              String(
                data.name || ''
              ).trim(),

            p_city:
              String(
                data.city || ''
              ).trim(),

            p_state:
              String(
                data.state || ''
              ).trim(),

            p_website_url:
              String(
                data.website_url || ''
              ).trim(),

            p_country:
              'India'
          }
        }
      );


    if (
      Array.isArray(result)
    ) {
      return result[0] || null;
    }


    return result || null;

  }



  async function getCollegeById(
    id
  ) {

    const rows =
      await request(
        'colleges',
        [
          'select=*',
          `id=eq.${encodeURIComponent(
            id
          )}`,
          'is_published=eq.true',
          'limit=1'
        ].join('&')
      );


    return rows?.[0] || null;

  }


  async function getCollegeBySlug(
    slug
  ) {

    const rows =
      await request(
        'colleges',
        [
          'select=*',
          `slug=eq.${encodeURIComponent(
            slug
          )}`,
          'is_published=eq.true',
          'limit=1'
        ].join('&')
      );


    return rows?.[0] || null;

  }


  async function getCollegeComparison(
    collegeId
  ) {

    const rows =
      await request(
        'college_comparison',
        [
          'select=*',
          `college_id=eq.${encodeURIComponent(
            collegeId
          )}`,
          'limit=1'
        ].join('&')
      );


    return rows?.[0] || null;

  }


  async function getPublicMentorBySlug(
    slug
  ) {

    const result =
      await request(
        'rpc/get_public_mentor_by_slug',
        '',
        {
          method:'POST',

          body:{
            p_slug:
              String(slug || '').trim()
          }
        }
      );


    if (
      Array.isArray(result)
    ) {
      return result[0] || null;
    }


    return result || null;

  }



  async function getPublishedMentors(
    limit = 24
  ) {

    const result =
      await request(
        'rpc/list_public_mentors',
        '',
        {
          method:'POST',

          body:{
            p_limit:
              Number(limit) || 24
          }
        }
      );


    return Array.isArray(result)
      ? result
      : [];

  }


  // The public directory must not stop at an arbitrary 24/100 mentors.
  // The current listing RPC takes p_limit but not a page offset, so grow
  // the requested result window until its size is smaller than requested.
  // Keep the existing RPC's approval/visibility/ordering rules unchanged.
  async function getAllPublishedMentors() {

    let requested = 64;
    let previousCount = 0;

    for (;;) {
      const rows = await getPublishedMentors(requested);

      if (!Array.isArray(rows)) {
        throw new Error('[PeerNest] Invalid public mentor list response.');
      }

      if (rows.length < previousCount) {
        throw new Error('[PeerNest] Public mentor listing changed while loading.');
      }

      if (rows.length < requested) {
        return rows;
      }

      // Do not silently show only the first N if the server stops growing.
      if (rows.length === previousCount) {
        throw new Error('[PeerNest] Public mentor RPC has a server-side cap; add RPC pagination.');
      }

      if (requested > 1000000000) {
        throw new Error('[PeerNest] Public mentor RPC needs offset pagination for larger datasets.');
      }

      previousCount = rows.length;
      requested *= 2;
    }

  }


  async function getCollegeMentors(
    collegeId,
    limit = 12
  ) {

    const result =
      await request(
        'rpc/list_public_mentors_by_college',
        '',
        {
          method:'POST',

          body:{
            p_college_id:
              collegeId,

            p_limit:
              Number(limit) || 12
          }
        }
      );


    return Array.isArray(result)
      ? result
      : [];

  }




  /* ========================================================
     PHASE 4 — MENTOR APPLICATION
  ======================================================== */

  async function uploadMentorApplicationPhoto(
    file
  ) {

    const user =
      await getCurrentUser();


    if (!user?.id) {
      throw new Error(
        'You must be signed in.'
      );
    }


    if (
      ![
        'image/jpeg',
        'image/png',
        'image/webp'
      ].includes(
        file?.type
      )
    ) {
      throw new Error(
        'Use a JPG, PNG or WebP image.'
      );
    }


    if (
      file.size >
      5 * 1024 * 1024
    ) {
      throw new Error(
        'Mentor photo must be under 5 MB.'
      );
    }


    const extension =
      file.type === 'image/png'
        ? 'png'
        : file.type === 'image/webp'
          ? 'webp'
          : 'jpg';


    const objectPath =
      `${user.id}/${crypto.randomUUID()}.${extension}`;


    await profileStorageRequest(
      `object/mentor-application-photos/${objectPath}`,
      {
        method:'POST',

        headers:{
          'Content-Type':
            file.type
        },

        body:file
      }
    );


    return objectPath;

  }



  async function uploadMentorApplicationDocument(
    file
  ) {

    const user =
      await getCurrentUser();


    if (!user?.id) {
      throw new Error(
        'You must be signed in.'
      );
    }


    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/webp',
      'application/pdf'
    ];


    if (
      !allowedTypes.includes(
        file?.type
      )
    ) {
      throw new Error(
        'College ID must be a JPG, PNG, WebP or PDF.'
      );
    }


    if (
      file.size >
      10 * 1024 * 1024
    ) {
      throw new Error(
        'College ID must be under 10 MB.'
      );
    }


    const extension =
      file.type === 'application/pdf'
        ? 'pdf'
        : file.type === 'image/png'
          ? 'png'
          : file.type === 'image/webp'
            ? 'webp'
            : 'jpg';


    const objectPath =
      `${user.id}/${crypto.randomUUID()}.${extension}`;


    await profileStorageRequest(
      `object/mentor-application-documents/${objectPath}`,
      {
        method:'POST',

        headers:{
          'Content-Type':
            file.type
        },

        body:file
      }
    );


    return objectPath;

  }



  async function submitMentorApplication(
    data = {}
  ) {

    const user =
      await getCurrentUser();


    if (!user?.id) {
      throw new Error(
        'You must be signed in to apply.'
      );
    }


    const result =
      await request(
        'rpc/submit_mentor_application_v2',
        '',
        {
          method:'POST',

          headers:{
            Prefer:'return=representation'
          },

          body:{
            p_college_id:
              String(
                data.college_id || ''
              ).trim(),

            p_program_name:
              data.program_name || '',

            p_graduation_year:
              data.graduation_year
                ? Number(
                    data.graduation_year
                  )
                : null,

            p_linkedin_url:
              String(
                data.linkedin_url || ''
              ).trim() || null,

            p_expertise_text:
              Array.isArray(
                data.expertise_text
              )
                ? data.expertise_text
                : [],

            p_languages_text:
              Array.isArray(
                data.languages_text
              )
                ? data.languages_text
                : [],

            p_achievements_text:
              Array.isArray(
                data.achievements_text
              )
                ? data.achievements_text
                : [],

            p_reason_for_mentoring:
              String(
                data.reason_for_mentoring || ''
              ).trim() || null,

            p_photo_url:
              String(
                data.photo_url || ''
              ).trim() || null,

            p_college_id_document_url:
              String(
                data.college_id_document_url || ''
              ).trim() || null
          }
        }
      );


    if (
      Array.isArray(result)
    ) {
      return result[0] || null;
    }


    return result || null;

  }



  async function getMyMentorApplication() {

    const user =
      await getCurrentUser();


    if (!user?.id) {
      return null;
    }


    const rows =
      await request(
        'mentor_applications',
        [
          'select=*',
          `user_id=eq.${encodeURIComponent(
            user.id
          )}`,
          'order=created_at.desc',
          'limit=1'
        ].join('&')
      );


    return rows?.[0] || null;

  }







  /* ========================================================
     PHASE 4D — MENTOR ONBOARDING
  ======================================================== */

  async function getMyMentorProfile() {

    const user =
      await getCurrentUser();


    if (!user?.id) {
      return null;
    }


    const rows =
      await request(
        'mentor_profiles',
        [
          'select=*',
          `user_id=eq.${encodeURIComponent(
            user.id
          )}`,
          'limit=1'
        ].join('&')
      );


    return rows?.[0] || null;

  }



  async function getExpertiseTags() {

    const rows =
      await request(
        'expertise_tags',
        [
          'select=id,slug,name',
          'order=name.asc'
        ].join('&')
      );


    return Array.isArray(rows)
      ? rows
      : [];

  }



  async function getLanguages() {

    const rows =
      await request(
        'languages',
        [
          'select=id,name',
          'order=name.asc'
        ].join('&')
      );


    return Array.isArray(rows)
      ? rows
      : [];

  }



  async function uploadMyMentorPhoto(
    file
  ) {

    const user =
      await getCurrentUser();


    if (!user?.id) {
      throw new Error(
        'You must be signed in.'
      );
    }


    if (
      ![
        'image/jpeg',
        'image/png',
        'image/webp'
      ].includes(
        file?.type
      )
    ) {
      throw new Error(
        'Use a JPG, PNG or WebP image.'
      );
    }


    if (
      file.size >
      5 * 1024 * 1024
    ) {
      throw new Error(
        'Mentor photo must be under 5 MB.'
      );
    }


    const objectPath =
      `${user.id}/profile`;


    await profileStorageRequest(
      `object/mentor-photos/${objectPath}`,
      {
        method:'POST',

        headers:{
          'Content-Type':
            file.type,

          'x-upsert':'true'
        },

        body:file
      }
    );


    return (
      `${baseURL}/storage/v1/object/public/` +
      `mentor-photos/${objectPath}` +
      `?v=${Date.now()}`
    );

  }



  async function completeMyMentorOnboarding(
    data = {}
  ) {

    const result =
      await request(
        'rpc/complete_my_mentor_onboarding_v3',
        '',
        {
          method:'POST',

          headers:{
            Prefer:'return=representation'
          },

          body:{

            p_display_name:
              String(
                data.display_name || ''
              ).trim(),

            p_bio:
              String(
                data.bio || ''
              ).trim(),

            p_graduation_year:
              data.graduation_year
                ? Number(
                    data.graduation_year
                  )
                : null,

            p_linkedin_url:
              String(
                data.linkedin_url || ''
              ).trim(),

            p_photo_url:
              String(
                data.photo_url || ''
              ).trim(),

            p_expertise_ids:
              Array.isArray(
                data.expertise_ids
              )
                ? data.expertise_ids
                : [],

            p_language_ids:
              Array.isArray(
                data.language_ids
              )
                ? data.language_ids
                : [],

            p_custom_expertise_text:
              String(
                data.custom_expertise_text ||
                ''
              ).trim(),

            p_custom_language_text:
              String(
                data.custom_language_text ||
                ''
              ).trim(),

            p_achievements:
              Array.isArray(
                data.achievements
              )
                ? data.achievements
                : [],

            p_session_duration_minutes:
              30,


            p_default_meet_url:
              String(
                data.default_meet_url || ''
              ).trim(),

            p_location_text:
              String(
                data.location_text || ''
              ).trim()

          }
        }
      );


    if (
      Array.isArray(result)
    ) {
      return result[0] || null;
    }


    return result || null;

  }



  /* ========================================================
     PHASE 4E — MENTOR AVAILABILITY
  ======================================================== */

  async function getMyMentorAvailability() {

    const result =
      await request(
        'rpc/get_my_mentor_availability',
        '',
        {
          method:'POST',
          body:{}
        }
      );


    if (
      Array.isArray(result) &&
      result.length === 1 &&
      Array.isArray(result[0])
    ) {
      return result[0];
    }


    return Array.isArray(result)
      ? result
      : [];

  }



  async function createMyAvailabilityBlock(
    data = {}
  ) {

    const result =
      await request(
        'rpc/create_my_availability_block',
        '',
        {
          method:'POST',

          headers:{
            Prefer:'return=representation'
          },

          body:{

            p_starts_at:
              data.starts_at,

            p_ends_at:
              data.ends_at,

            p_timezone:
              String(
                data.timezone ||
                'Asia/Kolkata'
              )

          }
        }
      );


    if (
      Array.isArray(result)
    ) {
      return result[0] || null;
    }


    return result || null;

  }



  async function removeMyAvailabilityBlock(
    blockId
  ) {

    const result =
      await request(
        'rpc/remove_my_availability_block',
        '',
        {
          method:'POST',

          body:{
            p_block_id:
              blockId
          }
        }
      );


    if (
      Array.isArray(result)
    ) {
      return result[0] ?? false;
    }


    return result === true;

  }



  async function removeMyAvailabilitySlot(
    slotId
  ) {

    const result =
      await request(
        'rpc/remove_my_availability_slot',
        '',
        {
          method:'POST',

          body:{
            p_slot_id:
              slotId
          }
        }
      );


    if (Array.isArray(result)) {
      return result[0] ?? false;
    }


    return result === true;

  }



  async function setMyMentorVisibility(
    visible
  ) {

    const result =
      await request(
        'rpc/set_my_mentor_visibility',
        '',
        {
          method:'POST',

          headers:{
            Prefer:'return=representation'
          },

          body:{
            p_visible:
              Boolean(visible)
          }
        }
      );


    if (Array.isArray(result)) {
      return result[0] || null;
    }


    return result || null;

  }



  async function adminMarkMentorApplicationUnderReview(
    applicationId
  ) {

    const result =
      await request(
        'rpc/admin_mark_mentor_application_under_review',
        '',
        {
          method:'POST',

          headers:{
            Prefer:'return=representation'
          },

          body:{
            p_application_id:
              applicationId
          }
        }
      );


    return Array.isArray(result)
      ? result[0] || null
      : result || null;

  }



  async function adminApproveMentorApplication(
    applicationId,
    adminNotes = null
  ) {

    const result =
      await request(
        'rpc/admin_approve_mentor_application',
        '',
        {
          method:'POST',

          headers:{
            Prefer:'return=representation'
          },

          body:{
            p_application_id:
              applicationId,

            p_admin_notes:
              adminNotes || null
          }
        }
      );


    return Array.isArray(result)
      ? result[0] || null
      : result || null;

  }



  async function adminRejectMentorApplication(
    applicationId,
    adminNotes = null
  ) {

    const result =
      await request(
        'rpc/admin_reject_mentor_application',
        '',
        {
          method:'POST',

          headers:{
            Prefer:'return=representation'
          },

          body:{
            p_application_id:
              applicationId,

            p_admin_notes:
              adminNotes || null
          }
        }
      );


    return Array.isArray(result)
      ? result[0] || null
      : result || null;

  }



  async function adminGetMentorApplicationFileURL(
    bucket,
    objectPath,
    expiresIn = 600
  ) {

    const allowedBuckets =
      [
        'mentor-application-photos',
        'mentor-application-documents'
      ];


    if (
      !allowedBuckets.includes(
        bucket
      )
    ) {
      throw new Error(
        'Invalid mentor application storage bucket.'
      );
    }


    if (!objectPath) {
      return null;
    }


    const result =
      await profileStorageRequest(
        `object/sign/${bucket}/${objectPath}`,
        {
          method:'POST',

          headers:{
            'Content-Type':
              'application/json'
          },

          body:
            JSON.stringify({
              expiresIn
            })
        }
      );


    const signed =
      result?.signedURL ||
      result?.signedUrl ||
      null;


    if (!signed) {
      return null;
    }


    return signed.startsWith('http')
      ? signed
      : `${baseURL}/storage/v1${signed}`;

  }



  async function adminGetDashboardStats() {

    const result =
      await request(
        'rpc/admin_get_dashboard_stats',
        '',
        {
          method:'POST',

          headers:{
            Prefer:'return=representation'
          },

          body:{}
        }
      );


    if (Array.isArray(result)) {
      return result[0] || {};
    }


    return result || {};

  }



  async function isCurrentUserAdmin() {

    const result =
      await request(
        'rpc/is_current_user_admin',
        '',
        {
          method:'POST',

          headers:{
            Prefer:'return=representation'
          },

          body:{}
        }
      );


    if (Array.isArray(result)) {
      return Boolean(
        result[0]
      );
    }


    return Boolean(
      result
    );

  }



  async function adminListMentorApplications(
    status = null
  ) {

    const result =
      await request(
        'rpc/admin_list_mentor_applications',
        '',
        {
          method:'POST',

          headers:{
            Prefer:'return=representation'
          },

          body:{
            p_status:
              status || null
          }
        }
      );


    return Array.isArray(result)
      ? result
      : [];

  }



  async function invokeFunction(
    name,
    body = {}
  ) {

    const session =
      await getSession();

    if (!session?.access_token) {
      throw new Error(
        'Sign in to use this PeerNest feature.'
      );
    }

    const response =
      await fetch(
        `${baseURL}/functions/v1/${encodeURIComponent(name)}`,
        {
          method:'POST',
          headers:{
            apikey:publishableKey,
            Authorization:`Bearer ${session.access_token}`,
            'Content-Type':'application/json',
            Accept:'application/json'
          },
          body:JSON.stringify(body)
        }
      );

    const text =
      await response.text();

    let result = null;

    try {
      result =
        text
          ? JSON.parse(text)
          : null;
    }
    catch (_) {
      result = {
        error:text
      };
    }

    if (!response.ok) {
      throw new Error(
        result?.error ||
        `PeerNest function failed (${response.status})`
      );
    }

    return result;

  }


  /* ========================================================
     PN8A_EVENT_COVER_UPLOAD
  ======================================================== */

  async function uploadEventCover(
    file
  ) {

    const user =
      await getCurrentUser();


    if (!user?.id) {
      throw new Error(
        'You must be signed in.'
      );
    }


    if (
      ![
        'image/jpeg',
        'image/png',
        'image/webp'
      ].includes(
        file?.type
      )
    ) {

      throw new Error(
        'Use a JPG, PNG or WebP event cover.'
      );

    }


    if (
      file.size >
      6 * 1024 * 1024
    ) {

      throw new Error(
        'Event cover must be under 6 MB.'
      );

    }


    const extension =
      file.type === 'image/png'
        ? 'png'
        : file.type === 'image/webp'
          ? 'webp'
          : 'jpg';


    const objectPath =
      `covers/${crypto.randomUUID()}.${extension}`;


    await profileStorageRequest(
      `object/event-covers/${objectPath}`,
      {
        method:'POST',

        headers:{
          'Content-Type':
            file.type
        },

        body:file
      }
    );


    return (
      `${baseURL}/storage/v1/object/public/` +
      `event-covers/${objectPath}` +
      `?v=${Date.now()}`
    );

  }



  /* ========================================================
     PN8A_COMMUNITY_COVER_UPLOAD
  ======================================================== */

  async function uploadCommunityCover(
    file
  ) {

    const user =
      await getCurrentUser();


    if (!user?.id) {
      throw new Error(
        'You must be signed in.'
      );
    }


    if (
      ![
        'image/jpeg',
        'image/png',
        'image/webp'
      ].includes(
        file?.type
      )
    ) {

      throw new Error(
        'Use a JPG, PNG or WebP community cover.'
      );

    }


    if (
      file.size >
      6 * 1024 * 1024
    ) {

      throw new Error(
        'Community cover must be under 6 MB.'
      );

    }


    const extension =
      file.type === 'image/png'
        ? 'png'
        : file.type === 'image/webp'
          ? 'webp'
          : 'jpg';


    const objectPath =
      `covers/${crypto.randomUUID()}.${extension}`;


    await profileStorageRequest(
      `object/community-covers/${objectPath}`,
      {
        method:'POST',

        headers:{
          'Content-Type':
            file.type
        },

        body:file
      }
    );


    return (
      `${baseURL}/storage/v1/object/public/` +
      `community-covers/${objectPath}` +
      `?v=${Date.now()}`
    );

  }



  /* ========================================================
     EXPOSE ONE SHARED CLIENT
  ======================================================== */

  window.PeerNestDB =
    Object.freeze({

      request,
      invokeFunction,

      signUp,
      signIn,
      signOut,
      requestPasswordReset,
      updatePassword,
      signInWithOAuth,
      consumeOAuthRedirect,

      getSession,
      refreshSession,

      getCurrentUser,
      getMyProfile,
      updateMyProfileDetails,
      uploadMyAvatar,
      uploadCollegeMedia,
      removeMyAvatar,
      uploadMyResume,
      getMyResumeURL,
      removeMyResume,
      getMyProjects,
      createMyProject,
      updateMyProject,
      deleteMyProject,
      updateMyProfile,

      getPublishedColleges,
      searchPeerNestColleges,
      requestMissingCollege,
      getCollegeById,
      getCollegeBySlug,
      getCollegeComparison,
      getCollegeMentors,
      getPublishedMentors,
      getAllPublishedMentors,
      getPublicMentorBySlug,

      uploadMentorApplicationPhoto,
      uploadMentorApplicationDocument,
      submitMentorApplication,
      getMyMentorApplication,

      getMyMentorProfile,
      getExpertiseTags,
      getLanguages,
      uploadMyMentorPhoto,
      uploadEventCover,
      uploadCommunityCover,
      completeMyMentorOnboarding,

      getMyMentorAvailability,
      createMyAvailabilityBlock,
      removeMyAvailabilityBlock,
      removeMyAvailabilitySlot,
      setMyMentorVisibility,

      adminMarkMentorApplicationUnderReview,
      adminApproveMentorApplication,
      adminRejectMentorApplication,
      adminGetMentorApplicationFileURL,
      adminGetDashboardStats,
      isCurrentUserAdmin,
      adminListMentorApplications

    });


  document.dispatchEvent(
    new CustomEvent(
      'peernest:supabase-ready'
    )
  );


  console.log(
    '[PeerNest] Supabase + Auth ready.'
  );

})();
